/**
 * ClientTargetPicker — BLU-3xx Phase 47.01.2-B-04
 *
 * Single-org target picker for inventory write modals (Receive Stock,
 * Individual Stocktake, Adjust Unavailable) at shared 3PL locations.
 *
 * Distinct from `ClientFilterDropdown` (B-03):
 *  - Filter is OPTIONAL with an "All clients" sentinel (it narrows a view).
 *  - This picker is REQUIRED with NO default (it routes a write).
 *
 * Design discipline:
 *  - Required + no default — caller MUST pick exactly one. Submit is gated
 *    until `value !== null` (handled by parent).
 *  - Attention-getting trigger when empty (`border-amber-300 ring-amber-100`)
 *    PLUS helper text — color alone fails accessibility.
 *  - Own org listed first ("{Name} (You)"), connected clients alphabetical
 *    after, each with the brand-purple `<Link2 />` icon — anchors the
 *    cross-org provenance pattern (DESIGN.md §13.3 / §13.5).
 *
 * Data fetch shares cache with B-03's `useClientOrgFilter` because both call
 * `locations.listConnectedClientsAtLocation({ locationId })` with identical
 * params — React Query dedupes automatically.
 */
import { useMemo } from 'react';
import { Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { useConnectedClients } from '../hooks/useConnectedClients';

export interface ClientTargetPickerProps {
  /** null = nothing chosen yet (required + no default; blocks submit). */
  value: string | null;
  onChange: (orgId: string) => void;
  /** Caller's own org id; rendered as the first option. */
  ownOrgId: string;
  /** Caller's own org display name; rendered as "{name} (You)". */
  ownOrgName: string;
  /** Determines which connected clients are accessible at this location. */
  locationId: string;
  /** Default: true. Drives required-state visuals + helper text. */
  required?: boolean;
  disabled?: boolean;
  /** Default: "Receiving for". Caller may override (e.g. stocktake → "For"). */
  label?: string;
  /** Optional id attached to the trigger for label-trigger association. */
  id?: string;
  /**
   * BLU-669: optional subtext rendered under the own-org option (e.g. "no
   * catalog at this location"). Mirrors the shipped ClientFilterDropdown
   * precedent of signalling own-org emptiness BEFORE the operator commits to
   * a dead-end pick. The option stays enabled — a hard-disable with no
   * explanation is worse than a visible annotation.
   */
  ownOrgSubtext?: string;
}

export function ClientTargetPicker({
  value,
  onChange,
  ownOrgId,
  ownOrgName,
  locationId,
  required = true,
  disabled = false,
  label,
  id = 'client-target-picker',
  ownOrgSubtext,
}: ClientTargetPickerProps) {
  const { t } = useTranslation('inventory');
  const resolvedLabel = label ?? t('crossOrg.picker.labelReceiving');
  // Shared cache with `useClientOrgFilter` via `useConnectedClients` —
  // React Query dedupes the request when both render concurrently
  // (BLU-3xx Phase 47.01.2-B-fix MED-6).
  const { clients: connectedClients, isLoading } = useConnectedClients({ locationId });

  const isMissing = required && !value;

  // Resolve the display label for the currently-selected option. The shadcn
  // SelectValue renders the matching SelectItem's children when value matches,
  // but we want a richer rendering for the SELECTED state (icon + suffix), so
  // we drive the trigger label ourselves.
  const selectedDisplay = useMemo(() => {
    if (!value) return null;
    if (value === ownOrgId) return t('crossOrg.picker.youSuffix', { name: ownOrgName });
    const match = connectedClients.find((c) => c.id === value);
    return match?.name ?? null;
  }, [value, ownOrgId, ownOrgName, connectedClients, t]);

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-sm">
        {resolvedLabel}
        {required && (
          <span aria-hidden="true" className="text-destructive ml-0.5">
            *
          </span>
        )}
      </Label>
      <Select
        // BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 2 + BUG 8b): when `value`
        // transitions from a string to null (e.g. resetForm after submit),
        // Radix Select treats the new `undefined` as "switching to
        // uncontrolled" and keeps the stale display + emits the
        // "controlled → uncontrolled" warning. The `key` change on null
        // forces an unmount+remount so the trigger renders the placeholder
        // cleanly. Stable key when a real value is set, so user
        // interactions don't churn the component.
        key={value ?? `${id}-empty`}
        value={value ?? undefined}
        onValueChange={onChange}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          aria-required={required}
          aria-invalid={isMissing}
          aria-describedby={isMissing ? `${id}-helper` : undefined}
          className={cn(
            'transition-colors',
            isMissing &&
              'border-amber-300 ring-1 ring-amber-100/50 focus:ring-amber-200',
          )}
        >
          {selectedDisplay ? (
            <span className="flex items-center gap-2 min-w-0 truncate">
              {value !== ownOrgId && (
                <Link2
                  className="h-3.5 w-3.5 text-threadheads-purple shrink-0"
                  aria-hidden="true"
                />
              )}
              <span className="truncate">{selectedDisplay}</span>
            </span>
          ) : (
            <SelectValue
              placeholder={isLoading ? t('crossOrg.picker.loading') : t('crossOrg.picker.placeholder')}
            />
          )}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('crossOrg.picker.yourOrgGroup')}
            </SelectLabel>
            <SelectItem value={ownOrgId}>
              <span className="flex flex-col">
                <span>{t('crossOrg.picker.youSuffix', { name: ownOrgName })}</span>
                {ownOrgSubtext && (
                  <span className="text-xs text-muted-foreground">{ownOrgSubtext}</span>
                )}
              </span>
            </SelectItem>
          </SelectGroup>
          {connectedClients.length > 0 && (
            <>
              <SelectSeparator />
              <SelectGroup>
                <SelectLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('crossOrg.picker.connectedClientsGroup')}
                </SelectLabel>
                {connectedClients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    <span className="flex items-center gap-2">
                      <Link2
                        className="h-3.5 w-3.5 text-threadheads-purple shrink-0"
                        aria-hidden="true"
                      />
                      <span>{client.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </>
          )}
        </SelectContent>
      </Select>
      {isMissing && (
        <p id={`${id}-helper`} className="text-xs text-amber-700">
          {t('crossOrg.picker.helperRequired')}
        </p>
      )}
    </div>
  );
}

/**
 * Sticky context chip — render NEAR the modal title once a target is chosen.
 * Persists across form steps within the modal. NOT dismissible mid-form per
 * FE-6 (chip is provenance, not a control); to re-pick, the user changes
 * the picker dropdown.
 *
 * Brand-purple treatment matches DESIGN.md §13 cross-org provenance language:
 *  - For caller's own org: muted/no chip — the default state, no extra context
 *    is needed. Caller passes `isOwnOrg` so the parent decides whether to
 *    render at all.
 *  - For client orgs: brand-purple chip with Link2 icon.
 */
export interface ClientTargetChipProps {
  /** Display label, e.g. "Mini Zali" or "Threadheads (You)". */
  resolvedName: string;
  /** Default: "Receiving for". Drives the prefix label. */
  label?: string;
  /** Whether the resolved target is the caller's own org. */
  isOwnOrg?: boolean;
  className?: string;
}

export function ClientTargetChip({
  resolvedName,
  label,
  isOwnOrg = false,
  className,
}: ClientTargetChipProps) {
  const { t } = useTranslation('inventory');
  const resolvedLabel = label ?? t('crossOrg.picker.labelReceiving');
  // For own-org we still render a quiet chip so the user has unambiguous
  // confirmation of their choice — but NOT in brand-purple (that anchors
  // cross-org). Muted chip for own, brand-purple chip for client.
  // BLU-3xx Phase 47.01.2-B-fix HIGH-4: matches DESIGN.md §13.9 spec —
  // `rounded-full px-3 py-1` with a 30%-opacity brand-purple border on
  // client variants (parallels the muted border on own-org).
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border',
        isOwnOrg
          ? 'bg-muted text-muted-foreground border-border'
          : 'bg-threadheads-purple/10 text-threadheads-purple border-threadheads-purple/30',
        className,
      )}
    >
      {!isOwnOrg && (
        <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />
      )}
      <span className="font-medium">
        {resolvedLabel}: <span className="font-semibold">{resolvedName}</span>
      </span>
    </div>
  );
}
